import Typo from "@/src/components/atom/Typo";
import BreakWordTypo from "@/src/components/molecule/BreakWordTypo";
import SectionCardGrid from "@/src/components/molecule/SectionCardGrid";
import SectionCardRow from "@/src/components/molecule/SectionCardRow";
import { contractDetailApi } from "@/src/store/apis/contracts/contractDetail";
import { isUndefined } from "@/src/utils/is";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { styled } from "styled-components";
import dayjs from "dayjs";

function BuyerCard() {
  const { t } = useTranslation();
  const params = useParams();

  const CONTRACT_INFO_DATE_FORMAT = "YYYY-MM-DD";

  const {
    exporterItem,
    quantityAndUnit,
    amount,
    unitPriceAndUnit,
    lastShipmentDate,
    shippingTerms,
    shippingTermsRemark,
    paymentTerms,
    paymentTermsRemark,
    expireDate,
  } = contractDetailApi.endpoints.getContractDetail.useQueryState(
    {
      id: Number(params.id),
    },
    {
      selectFromResult: ({ currentData, isError, isFetching }) => {
        const isUnstable = isUndefined(currentData) || isError || isFetching;

        return {
          exporterItem: !isUnstable ? (currentData?.exporterItem ?? "-") : "-",
          quantityAndUnit:
            !isUnstable && currentData.quantity && currentData.quantityUnit
              ? `${Number(currentData.quantity).toLocaleString("ko-KR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} ${currentData.quantityUnit}`
              : "-",
          unitPriceAndUnit:
            !isUnstable && currentData.unitPrice && currentData.unitPriceUnit
              ? `${Number(currentData.unitPrice).toLocaleString("ko-KR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} ${currentData.unitPriceUnit}`
              : "-",
          amount:
            !isUnstable &&
            currentData.quantity &&
            currentData.unitPrice &&
            currentData.unitPriceUnit
              ? `${parseFloat(
                  (
                    Number(currentData.quantity) * Number(currentData.unitPrice)
                  ).toFixed(2)
                ).toLocaleString("ko-KR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} ${currentData.unitPriceUnit}`
              : "-",
          lastShipmentDate:
            !isUnstable && currentData.lastShipmentDateAt
              ? dayjs(currentData?.lastShipmentDateAt).format(
                  CONTRACT_INFO_DATE_FORMAT
                )
              : "-",
          shippingTerms: !isUnstable ? (currentData.shippingTerms ?? "-") : "-",
          shippingTermsRemark: !isUnstable
            ? (currentData.shippingTermsRemark ?? "")
            : "",
          paymentTerms: !isUnstable ? (currentData.paymentTerms ?? "-") : "-",
          paymentTermsRemark: !isUnstable
            ? (currentData.paymentTermsRemark ?? "")
            : "",
          expireDate:
            !isUnstable && currentData.expireDateAt
              ? dayjs(currentData.expireDateAt).format(
                  CONTRACT_INFO_DATE_FORMAT
                )
              : "-",
        };
      },
    }
  );

  return (
    <SectionCardGrid>
      {/* Item */}
      <StyledSectionCardThree
        label={
          <Typo color="gray6" typoType="b9m">
            {t("common:item")}
          </Typo>
        }
        direction="vertical"
        value={<BreakWordTypo typoType="h4">{exporterItem}</BreakWordTypo>}
      />

      {/* Qty */}
      <StyledSectionCardThree
        label={
          <Typo color="gray6" typoType="b9m">
            {t("common:qty")}
          </Typo>
        }
        direction="vertical"
        value={<BreakWordTypo typoType="h4">{quantityAndUnit}</BreakWordTypo>}
      />

      {/* Unit Price */}
      <StyledSectionCardThree
        label={
          <Typo color="gray6" typoType="b9m">
            {t("common:unitPrice")}
          </Typo>
        }
        direction="vertical"
        value={<BreakWordTypo typoType="h4">{unitPriceAndUnit}</BreakWordTypo>}
      />

      {/* Amount */}
      <StyledSectionCardThree
        label={
          <Typo color="gray6" typoType="b9m">
            {t("common:amount")}
          </Typo>
        }
        direction="vertical"
        value={<BreakWordTypo typoType="h4">{amount}</BreakWordTypo>}
      />

      {/* Last Shipment Date */}
      <StyledSectionCardThree
        label={
          <Typo color="gray6" typoType="b9m">
            {t("common:lastShipmentDate")}
          </Typo>
        }
        direction="vertical"
        value={<BreakWordTypo typoType="h4">{lastShipmentDate}</BreakWordTypo>}
      />

      {/* Shipping Terms */}
      <StyledSectionCardThree
        label={
          <Typo color="gray6" typoType="b9m">
            {t("common:shippingTerms")}
          </Typo>
        }
        direction="vertical"
        value={
          <ShippingTermsContainer>
            <Typo typoType="h4">{shippingTerms}</Typo>
            {shippingTermsRemark && (
              <BreakWordTypo typoType="h4">{shippingTermsRemark}</BreakWordTypo>
            )}
          </ShippingTermsContainer>
        }
      />

      {/* Payment Terms */}
      <StyledSectionCardThree
        label={
          <Typo color="gray6" typoType="b9m">
            {t("common:paymentTerms")}
          </Typo>
        }
        direction="vertical"
        value={
          <ShippingTermsContainer>
            <Typo typoType="h4">{paymentTerms}</Typo>
            {paymentTermsRemark && (
              <BreakWordTypo typoType="h4">{paymentTermsRemark}</BreakWordTypo>
            )}
          </ShippingTermsContainer>
        }
      />

      {paymentTerms === "L/C" && (
        <StyledSectionCardThree
          label={
            <Typo color="gray6" typoType="b9m">
              {t("common:expireDate")}
            </Typo>
          }
          direction="vertical"
          value={<BreakWordTypo typoType="h4">{expireDate}</BreakWordTypo>}
        />
      )}
    </SectionCardGrid>
  );
}

export default BuyerCard;

const StyledSectionCardThree = styled(SectionCardRow)`
  grid-column: 3 span;
`;

const ShippingTermsContainer = styled.div`
  display: flex;
  gap: 4px;
`;
